import {
  VisitDTO,
  VisitStatisticsDTO,
  VisitAnalyticsDTO,
  VisitItemDTO,
  VisitImageDTO,
  StoreVisitPerformanceDTO,
  VisitTrendsDTO
} from '../models/visit.dto';

import {
  Visit,
  VisitStatistics,
  VisitAnalytics,
  VisitItem,
  VisitImage,
  StoreVisitPerformance,
  VisitTrends
} from '../models/visit.model';

// Helper: parse location - supports {lat,lng} and GeoJSON {coordinates:[lng,lat]}
const parseStartLocation = (loc: VisitDTO['start_location'] | undefined): {lat: number; lng: number} | undefined => {
  if (!loc) return undefined;
  // GeoJSON Point: coordinates = [longitude, latitude]
  const coords = (loc as {coordinates?: number[]}).coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    const lng = coords[0];
    const lat = coords[1];
    if (isValidLat(lat) && isValidLng(lng)) return {lat, lng};
  }
  // Standard {lat, lng}
  const lat = (loc as {lat?: number}).lat;
  const lng = (loc as {lng?: number}).lng;
  if (lat != null && lng != null && isValidLat(lat) && isValidLng(lng)) {
    return {lat, lng};
  }
  return undefined;
};
const isValidLat = (n: number) => !isNaN(n) && n >= -90 && n <= 90;
const isValidLng = (n: number) => !isNaN(n) && n >= -180 && n <= 180;

// Helper: parse store coordinates - prefers lat/lng, fallback geo_location [lng,lat], then location
const parseStoreCoords = (store: VisitDTO['store']): {lat: number; lng: number} | null => {
  if (!store) return null;
  const geoCoords = store.geo_location?.coordinates;
  if (Array.isArray(geoCoords) && geoCoords.length >= 2) {
    const lng = geoCoords[0];
    const lat = geoCoords[1];
    if (isValidLat(lat) && isValidLng(lng)) return {lat, lng};
  }
  const lat = store.latitude ?? parseFloat(store.location?.lat ?? '');
  const lng = store.longitude ?? parseFloat(store.location?.lng ?? '');
  if (isValidLat(lat) && isValidLng(lng)) return {lat, lng};
  return null;
};

// Main Visit Mapper
export const mapVisitDTOToModel = (dto: VisitDTO): Visit => {
  // Add defensive programming to handle potential undefined values
  if (!dto) {
    throw new Error('VisitDTO is undefined or null');
  }

  if (!dto.id) {
    throw new Error('VisitDTO is missing required ID field');
  }
  const statusNorm = normalizeStatus(dto.status);

  const startLoc = parseStartLocation(dto.start_location);
  const storeCoords = parseStoreCoords(dto.store);

  return {
    id: dto.id,
    storeId: dto.store_id,
    visitorId: dto.visitor_id,
    brandId: dto.brand_id,
    visitDate: dto.visit_date,
    notes: dto.notes,
    status: statusNorm,
    approvedBy: dto.approved_by,
    approvedAt: dto.approved_at,
    visitData: dto.visit_data,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    startedAt: dto.started_at,
    startLocation: startLoc ? {
      lat: startLoc.lat,
      lng: startLoc.lng,
      accuracy: (dto.start_location as {accuracy?: number})?.accuracy
    } : undefined,
    rejectReason: dto.reject_reason ?? null,

    // Store Information - API doesn't include store details in visit response
    // Store details are shown separately in StoreDetails component
    store: {
      id: dto.store_id, // Use store_id from the visit
      storeName: dto.store?.store_name || 'المتجر',
      managerName: dto.store?.manager_name || '',
      phoneNumber: dto.store?.phone_number || '',
      latitude: storeCoords?.lat ?? dto.store?.latitude ?? 0,
      longitude: storeCoords?.lng ?? dto.store?.longitude ?? 0,
      city: dto.store?.city || '',
      region: dto.store?.region || '',
      notes: dto.store?.notes || '',
      profileImageUrl: dto.store?.profile_image_url || '',
      commercialRegisterNumber: dto.store?.commercial_register_number || '',
      commercialRegisterImage: dto.store?.commercial_register_image || '',
      taxCardNumber: dto.store?.tax_card_number || '',
      taxCardImage: dto.store?.tax_card_image || '',
      developerId: dto.store?.developer_id || 0,
      area: dto.store?.area || '',
      status: dto.store?.status || 'active',
      inventory: Array.isArray(dto.store?.inventory) ? dto.store.inventory.join(', ') : (dto.store?.inventory || ''),
      quantityAvailable: dto.store?.quantity_available || 0,
      lowStockThreshold: dto.store?.low_stock_threshold || 0,
      totalDue: String(dto.store?.total_due || 0),
      totalPaid: String(dto.store?.total_paid || 0),
      lastVisit: dto.store?.last_visit || '',
      lastPaymentDate: dto.store?.last_payment_date || '',
      creditLimit: String(dto.store?.credit_limit || 0),
      paymentRating: String(dto.store?.payment_rating || 0),
      totalSales: String(dto.store?.total_sales || 0)
    },

    // Visitor Information
    visitor: {
      id: dto.visitor?.id || 0,
      username: dto.visitor?.username || '',
      fullName: dto.visitor?.full_name || '',
      email: dto.visitor?.email || '',
      phoneNumber: dto.visitor?.phone_number || '',
      status: dto.visitor?.status || 'active'
    },

    // Brand Information - Not provided in API response, using defaults
    brand: {
      id: dto.brand?.id || 0,
      name: dto.brand?.name || 'غير محدد',
      logoUrl: dto.brand?.logo_url || '',
      description: dto.brand?.description || '',
      website: dto.brand?.website || ''
    },

    // Visit Items (Products)
    visitItems: dto.visitItems?.map(mapVisitItemDTOToModel) || [],

    // Visit Images
    visitImages: dto.visitImages?.map(mapVisitImageDTOToModel) || [],

    // Display Properties
    statusText: getStatusText(statusNorm),
    statusColor: getStatusColor(statusNorm)
  };
};

// Visit Item Mapper
export const mapVisitItemDTOToModel = (dto: VisitItemDTO): VisitItem => {
  const availabilityNorm = normalizeAvailability(dto.availability);
  const quantity = dto.quantity ?? dto.new_quantity ?? dto.previous_quantity ?? 0;
  const prevQty = dto.previous_quantity ?? 0;
  const newQty = dto.new_quantity ?? quantity;
  const change = dto.quantity_change ?? (newQty - prevQty);

  return {
    id: dto.id,
    visitId: dto.visit_id,
    productId: dto.product_id,
    productName: dto.product_name,
    brandId: dto.brand_id ?? 0,
    availability: availabilityNorm,
    quantity,
    previousQuantity: prevQty,
    newQuantity: newQty,
    quantityChange: change,
    notes: dto.notes,
    createdAt: dto.created_at
  };
};

// Visit Image Mapper
export const mapVisitImageDTOToModel = (dto: VisitImageDTO): VisitImage => {
  const imageUrl = dto.image_url ?? dto.url ?? '';
  return {
    id: dto.id,
    subjectId: dto.subject_id,
    subjectType: dto.subject_type,
    visitId: dto.visit_id,
    imageUrl,
    imageType: dto.image_type,
    description: dto.description,
    createdAt: dto.created_at ?? dto.captured_at,
    capturedAt: dto.captured_at ?? dto.created_at,
    location: dto.location,
    watermarkText: dto.watermark_text
  };
};

// Statistics Mapper
export const mapVisitStatisticsDTOToModel = (dto: VisitStatisticsDTO): VisitStatistics => {
  return {
    period: dto.period || 'monthly',
    dateRange: {
      startDate: dto.date_range?.start_date || new Date().toISOString(),
      endDate: dto.date_range?.end_date || new Date().toISOString()
    },
    statusDistribution: dto.status_distribution || [],
    dailyTrend: (dto.daily_trend || []).map(trend => ({
      date: trend.date,
      totalVisits: trend.total_visits,
      approvedVisits: trend.approved_visits,
      rejectedVisits: trend.rejected_visits
    })),
    hourlyDistribution: dto.hourly_distribution || [],
    topStores: (dto.top_stores || []).map(store => ({
      storeId: store.store_id,
      storeName: store.store_name,
      city: store.city,
      visitCount: store.visit_count
    })),
    topVisitors: (dto.top_visitors || []).map(visitor => ({
      visitorId: visitor.visitor_id,
      visitorName: visitor.visitor_name,
      username: visitor.username,
      visitCount: visitor.visit_count
    })),
    processingMetrics: {
      avgProcessingTimeHours: dto.processing_metrics?.avg_processing_time_hours || '0'
    }
  };
};

// Store Performance Mapper
export const mapStorePerformanceDTOToModel = (dto: StoreVisitPerformanceDTO): StoreVisitPerformance => {
  return {
    storeId: dto.store_id,
    storeName: dto.store_name,
    totalVisits: parseInt(dto.total_visits || '0'),
    completedVisits: parseInt(dto.completed_visits || '0')
  };
};

// Analytics Mapper
export const mapVisitAnalyticsDTOToModel = (dto: VisitAnalyticsDTO): VisitAnalytics => {
  return {
    period: dto.period,
    dateRange: {
      startDate: dto.date_range?.start_date || new Date().toISOString(),
      endDate: dto.date_range?.end_date || new Date().toISOString()
    },
    statusDistribution: dto.status_distribution || [],
    dailyTrend: (dto.daily_trend || []).map(trend => ({
      date: trend.date,
      totalVisits: trend.total_visits,
      approvedVisits: trend.approved_visits,
      rejectedVisits: trend.rejected_visits
    })),
    hourlyDistribution: dto.hourly_distribution || [],
    topStores: (dto.top_stores || []).map(store => ({
      storeId: store.store_id,
      storeName: store.store_name,
      city: store.city,
      visitCount: store.visit_count
    })),
    topVisitors: (dto.top_visitors || []).map(visitor => ({
      visitorId: visitor.visitor_id,
      visitorName: visitor.visitor_name,
      username: visitor.username,
      visitCount: visitor.visit_count
    })),
    processingMetrics: {
      avgProcessingTimeHours: dto.processing_metrics?.avg_processing_time_hours || '0'
    }
  };
};

// Visit Trends Mapper
export const mapVisitTrendsDTOToModel = (dto: VisitTrendsDTO): VisitTrends => {
  return {
    totalVisits: dto.total_visits,
    pendingVisits: dto.pending_visits,
    approvedVisits: dto.approved_visits,
    rejectedVisits: dto.rejected_visits,
    visitsThisMonth: dto.visits_this_month,
    visitsByBrand: (dto.visits_by_brand || []).map(brand => ({
      brandName: brand.brand_name,
      count: brand.count
    })),
    topStores: (dto.top_stores || []).map(store => ({
      storeId: store.store_id,
      storeName: store.store_name,
      visitCount: store.visit_count
    })),
    topVisitors: (dto.top_visitors || []).map(visitor => ({
      visitorId: visitor.visitor_id,
      visitorName: visitor.visitor_name,
      visitCount: visitor.visit_count
    }))
  };
};

// Helper Functions
const normalizeStatus = (status: string): 'pending' | 'approved' | 'rejected' => {
  const s = (status || '').toLowerCase();
  if (s === 'approved' || s === 'rejected' || s === 'pending') return s;
  return 'pending';
};

const normalizeAvailability = (availability?: string): 'AVAILABLE' | 'NOT_AVAILABLE' | undefined => {
  if (!availability) return undefined;
  const a = availability.toUpperCase();
  if (a === 'AVAILABLE' || a === 'NOT_AVAILABLE') return a;
  return a === 'NOT_AVAILABLE' ? 'NOT_AVAILABLE' : 'AVAILABLE';
};

const getStatusText = (status: string): string => {
  const statusMap: Record<string, string> = {
    'pending': 'في الانتظار',
    'approved': 'موافق عليها',
    'rejected': 'مرفوضة'
  };
  return statusMap[status] || status;
};

const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    'pending': 'text-yellow-600 bg-yellow-100',
    'approved': 'text-green-600 bg-green-100',
    'rejected': 'text-red-600 bg-red-100'
  };
  return colorMap[status] || 'text-gray-600 bg-gray-100';
};
